import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';

import { CombinedSettingsTypes } from '../../../../domain/settings';
import { StatsEntity } from '../../../../domain/stats';
import { StatsFilters as StatsFiltersType, TimePeriod } from '../../../../domain/statsFilters';
import { useFindAllUsersRepository } from '../../../../repository/users/useFindAllUsersRepository';
import { countActiveOrders } from '../specific-stats/ordersCount';
import { buildSearchParamsFromStatsFilters } from './buildSearchParamsFromStatsFilters';
import { buildStatsFiltersFromSearchParams } from './buildStatsFiltersFromSearchParams';
import { TimeFilterComponent } from './pickers/TimeFilterComponent';
import { TimePeriodSelector } from './pickers/TimePeriodSelector';

type StatsFiltersProps = {
    stats: StatsEntity;
    settings: CombinedSettingsTypes;
    className?: string;
}

export const StatsFilters = ({ stats, settings, className, }: StatsFiltersProps) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const filters = buildStatsFiltersFromSearchParams(searchParams);
    const [filtersState, setFiltersState] = useState<StatsFiltersType>(filters);

    useEffect(() => {
        setFiltersState(buildStatsFiltersFromSearchParams(searchParams));
    }, [searchParams]);

    const { data: usersAccounts = [] } = useFindAllUsersRepository();

    const contractorTransportOrdersCounts = stats.contractorsStats.map(({ id, name, orderCount }) => ({
        contractor: { id, name },
        count: countActiveOrders(orderCount),
    }));

    const timePeriod = filtersState.timePeriodSelector;
    const selectedUserId = filtersState.creator?.id || '';
    const selectedContractorId = filtersState.contractor?.id || '';


    const handleCreatorChange = (selectedCreatorId?: string) => {
        setFiltersState((prevState) => ({
            ...prevState,
            creator: selectedCreatorId ? { id: selectedCreatorId } : undefined,
        }));
    };

    const handleContractorChange = (selectedContractorId?: string) => {
        setFiltersState((prevState) => ({
            ...prevState,
            contractor: selectedContractorId ? { id: selectedContractorId } : undefined,
        }));
    };

    const handleTimePeriodChange = (timePeriod: TimePeriod, defaultTimePeriodRelatedFilterValue: Partial<StatsFiltersType>) => {
        setFiltersState((prevState) => ({
            ...prevState,
            ...defaultTimePeriodRelatedFilterValue,
            timePeriodSelector: timePeriod,
        }));
    }

    const handleSubmit = () => {
        setSearchParams(buildSearchParamsFromStatsFilters(filtersState));
    };

    const handleClear = () => {
        const defaultFilters = buildStatsFiltersFromSearchParams(new URLSearchParams());
        setSearchParams(buildSearchParamsFromStatsFilters(defaultFilters));
    };

    const usersAsOptions = usersAccounts.map((user) => ({
        value: user.sub,
        label: `${user.firstName} ${user.lastname}`,
    }));

    const selectedUser = usersAsOptions.find((user) => user.value === selectedUserId);

    const contractorsAsOptions = contractorTransportOrdersCounts.map(({ contractor, count }) => ({
        value: contractor.id,
        label: contractor.name,
        count,
    })).sort((a, b) => b.count - a.count);

    const selectedContractor = contractorsAsOptions.find((contractor) => contractor.value === selectedContractorId);

    return <div className={`d-flex flex-row align-items-end ${className}`}>
        <Form onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
        }}>
            <div className="d-flex flex-row align-items-end mb-1">
                <TimeFilterComponent
                    timePeriod={timePeriod}
                    filtersState={filtersState}
                    onChange={(newFilters) => setFiltersState(prev => ({ ...prev, ...newFilters }))}
                    settings={settings}
                />
                <div className="me-3">
                    <h6>Wyszukaj po spedytorze</h6>
                    <Select
                        key={`creators-select-${selectedUserId}`}
                        isClearable
                        placeholder="Wybierz"
                        options={usersAsOptions}
                        value={selectedUser}
                        onChange={(user) => handleCreatorChange(user?.value)}
                    />
                </div>
                <div className="me-3">
                    <h6>Wyszukaj po podwykonawcy</h6>
                    <Select
                        key={`contractors-select-${selectedContractorId}`}
                        isClearable
                        placeholder="Wybierz"
                        options={contractorsAsOptions}
                        value={selectedContractor}
                        onChange={(contractor) => handleContractorChange(contractor?.value)}
                    />
                </div>
                <Button className="me-2" variant="primary" type="submit">Szukaj</Button>
                <Button variant="outline-primary" onClick={handleClear}>Wyczyść</Button>
            </div>
            <Form.Group>
                <TimePeriodSelector timePeriod={timePeriod} onChange={handleTimePeriodChange} />
            </Form.Group>
        </Form>
    </div>;
};
